import COUNTRY_DATA from "../Data/countries.json";
import { normalizeCountryInput } from "./countryOptions";

const VALID_CODES = new Set(COUNTRY_DATA.map((country) => country.code));
const INVALID_CODES = new Set(["XX", "ZZ"]);

const COUNTRY_BY_CODE = new Map(
  COUNTRY_DATA.map((country) => [country.code, country.label])
);

const normalizeKey = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Locales used to resolve country names typed in other languages. */
const FLAG_LOCALES = [
  "en-US",
  "en",
  "ar",
  "fr",
  "de",
  "es",
  "pt",
  "ru",
  "zh",
  "ja",
  "hi",
  "tr",
  "it",
  "nl",
  "ko",
  "pl",
  "uk",
  "vi",
  "th",
  "id",
  "ms",
  "fa",
  "he",
  "sv",
  "no",
  "da",
  "fi",
  "cs",
  "ro",
  "hu",
  "el",
  "bg",
];

let localizedNameToCode = null;

function buildLocalizedNameLookup() {
  const map = new Map();

  // Seed English labels from our static list first (Turkey, not only Türkiye).
  COUNTRY_DATA.forEach((country) => {
    if (!country?.code || !country?.label || INVALID_CODES.has(country.code)) return;
    const key = normalizeKey(country.label);
    if (key) map.set(key, country.code);
  });

  if (typeof Intl === "undefined" || typeof Intl.DisplayNames !== "function") {
    return map;
  }

  FLAG_LOCALES.forEach((locale) => {
    try {
      const displayNames = new Intl.DisplayNames([locale], { type: "region" });
      VALID_CODES.forEach((code) => {
        if (INVALID_CODES.has(code)) return;
        const name = displayNames.of(code);
        if (!name || name === code) return;
        const key = normalizeKey(name);
        if (key && !map.has(key)) {
          map.set(key, code);
        }
      });
    } catch {
      // Skip unsupported locale in this environment.
    }
  });

  return map;
}

function getLocalizedNameLookup() {
  if (!localizedNameToCode) {
    localizedNameToCode = buildLocalizedNameLookup();
  }
  return localizedNameToCode;
}

function isValidIsoCode(code) {
  return Boolean(code) && /^[A-Z]{2}$/.test(code) && VALID_CODES.has(code) && !INVALID_CODES.has(code);
}

/**
 * Resolve participant country input (ISO code, English name, alias, or
 * localized name) to an ISO-3166 alpha-2 code.
 */
export function resolveCountryCode(countryInput = "", countryCode = "") {
  const fromCode = String(countryCode || "")
    .trim()
    .toUpperCase();
  if (isValidIsoCode(fromCode)) {
    return fromCode;
  }

  const raw = String(countryInput ?? "").trim();
  if (!raw) return "";

  const upper = raw.toUpperCase();
  if (isValidIsoCode(upper)) {
    return upper;
  }

  // English label / known aliases (Turkey + Türkiye both map to TR)
  const canonicalLabel = normalizeCountryInput(raw);
  if (canonicalLabel) {
    const match = COUNTRY_DATA.find((item) => item.label === canonicalLabel);
    if (match?.code && isValidIsoCode(match.code)) return match.code;
  }

  // Any-language country names via Intl.DisplayNames (+ seeded English labels)
  const localizedCode = getLocalizedNameLookup().get(normalizeKey(raw));
  if (localizedCode && isValidIsoCode(localizedCode)) return localizedCode;

  // Legacy rows stored the country name truncated to 8 characters, so fall back
  // to a unique prefix match ("afghanis" -> "afghanistan").
  const key = normalizeKey(raw);
  if (key.length >= 4) {
    const prefixMatches = [];
    getLocalizedNameLookup().forEach((code, name) => {
      if (name.startsWith(key) && isValidIsoCode(code) && !prefixMatches.includes(code)) {
        prefixMatches.push(code);
      }
    });
    if (prefixMatches.length === 1) return prefixMatches[0];
  }

  return "";
}

export function resolveCountryLabel(countryInput = "", countryCode = "") {
  const code = resolveCountryCode(countryInput, countryCode);
  if (code && COUNTRY_BY_CODE.has(code)) {
    return COUNTRY_BY_CODE.get(code);
  }
  return String(countryInput || countryCode || "").trim();
}

/** Same CDN pattern as LogzMarketing: flagcdn.com/w40/{code}.png */
export function getCountryFlagUrl(countryInput = "", countryCode = "") {
  const code = resolveCountryCode(countryInput, countryCode);
  if (!code) return "";
  return `https://flagcdn.com/w40/${code.toLowerCase()}.png`;
}

export function resolveCountryFlag(countryInput = "", countryCode = "") {
  const code = resolveCountryCode(countryInput, countryCode);
  return {
    code,
    label: resolveCountryLabel(countryInput, countryCode),
    flagUrl: code ? `https://flagcdn.com/w40/${code.toLowerCase()}.png` : "",
  };
}
